'use client'

import Link from 'next/link'
import type {
  ComponentProps,
  ElementType,
  MouseEvent,
  PropsWithChildren,
} from 'react'

import { cn, createSlot } from '@/shared/lib'
import { Image } from '@/shared/ui'

type TitleProps = {
  className?: string
}

export const Title = ({
  children,
  className,
}: PropsWithChildren<TitleProps>) => {
  return (
    <h3 className={cn('typo-headline2 font-semibold text-black', className)}>
      {children}
    </h3>
  )
}

export const List = ({ children }: PropsWithChildren) => {
  return <ul className="space-y-5">{children}</ul>
}

type ItemProps<E extends ElementType> = {
  as?: E | typeof Link
  size?: 'small' | 'large'
  closed?: boolean
  className?: string
}

type ItemComponentProps<E extends ElementType> = ItemProps<E> &
  ComponentProps<E>

export const Item = <E extends ElementType>({
  as,
  closed = false,
  size = 'large',
  className,
  children,
  ...restProps
}: PropsWithChildren<ItemComponentProps<E>>) => {
  const Component = as || Link

  const { slots } = createSlot(children, [
    'image',
    'category',
    'title',
    'subContent',
  ])

  const imageSize = (() => {
    if (size === 'small') {
      return {
        width: 150,
        height: 100,
      }
    }

    return {
      width: 361,
      height: 122,
    }
  })()

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (closed) {
      event.preventDefault()
    }
  }

  return (
    <li>
      <Component
        className={cn(
          'flex',
          {
            'gap-4': size === 'small',
            'gap-6': size === 'large',
            'cursor-default': closed,
          },
          className,
        )}
        onClick={handleClick}
        {...restProps}
      >
        {slots.image && (
          <Image
            src={slots.image as string}
            alt={slots.title as string}
            width={imageSize.width}
            height={imageSize.height}
            unoptimized
            placeholder
            className={cn('shrink-0', {
              'opacity-50': closed,
              'rounded-sm': size === 'small',
              'rounded-lg': size === 'large',
            })}
          />
        )}
        <div className="flex flex-col justify-center space-y-1.5">
          {slots.category && (
            <p className="typo-label2 font-medium text-purple-500">
              {slots.category}
            </p>
          )}
          {slots.title && (
            <h4
              className={cn('typo-body1 font-semibold text-gray-900', {
                'text-gray-400': closed,
                'line-clamp-2': size === 'small',
                'line-clamp-1': size === 'large',
              })}
            >
              {slots.title}
            </h4>
          )}
          {slots.subContent && (
            <p
              className={cn('typo-label1 font-normal text-gray-500', {
                'text-gray-400': closed,
                'line-clamp-1': size === 'small',
                'line-clamp-2': size === 'large',
              })}
            >
              {slots.subContent}
            </p>
          )}
        </div>
      </Component>
    </li>
  )
}

export const Empty = ({ children }: PropsWithChildren) => {
  return (
    <div className="flex h-45 items-center justify-center text-gray-400">
      {children}
    </div>
  )
}
